/**
 * IndexedDB-backed call-link revocation index.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallLinkStore.swift`.
 *
 * Persists revocation records (indexed by roomId) used by
 * `CallLinkPolicy.evaluateCallLinkPolicy` to reject redemption of dead links.
 *
 * Hardening rules (mirror iOS exactly):
 *   - `recordRevocation` verifies the embedded Ed25519 signature against the
 *     supplied owner public key BEFORE storing. A revocation that fails
 *     verification is dropped and returns false; the store therefore never
 *     reflects unauthenticated revocations even if the server tries to inject
 *     one.
 *   - The store is intentionally append-only for revocations: once a roomId is
 *     revoked locally, the only way to forget it is the explicit `resetAll`.
 *     There is no "unrevoke."
 *
 * Storage:
 *   - IndexedDB via idb-keyval.
 *   - Revocations keyed by "revoke:<sha256hex(roomId.raw)>" for per-link
 *     isolation (mirrors iOS's SHA256-named files).
 *   - Known links keyed by "link:<sha256hex(roomId.raw)>" (optional history).
 *
 * Pure logic / SubtleCrypto / IndexedDB only. No SolidJS imports.
 */

import { get, set, del, keys, createStore } from "idb-keyval";
import type { UseStore } from "idb-keyval";
import {
  type CallLink,
  CallLinkRoomId,
  deriveRoomId,
  base64UrlDecode,
} from "./CallLink";
import {
  type CallLinkRevocationPayload,
  verifyCallLinkRevocation,
} from "./CallLinkRevocation";

// ---------------------------------------------------------------------------
// Stored record types
// ---------------------------------------------------------------------------

interface StoredRevocation {
  readonly schema: number;
  readonly roomId: string; // base64url
  readonly revokedAt: number;
  readonly ownerSignature: string; // base64
}

interface StoredKnownLink {
  readonly roomId: string;
  readonly ownerUserId: string;
  readonly ownerPublicKey: string; // base64url
  readonly flags: number;
  readonly createdAt: string; // BigInt serialised as decimal string
}

// ---------------------------------------------------------------------------
// Key helpers
// ---------------------------------------------------------------------------

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const hashBuf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function revocationKey(roomId: CallLinkRoomId): Promise<string> {
  return "revoke:" + (await sha256Hex(roomId.raw));
}

async function knownLinkKey(roomId: CallLinkRoomId): Promise<string> {
  return "link:" + (await sha256Hex(roomId.raw));
}

// ---------------------------------------------------------------------------
// CallLinkStore
// ---------------------------------------------------------------------------

/**
 * Options for constructing a `CallLinkStore`. Provide `idbStore` to inject a
 * custom idb-keyval store (useful for test isolation with unique DB names).
 */
export interface CallLinkStoreOptions {
  idbStore?: UseStore;
}

/**
 * Local cache of call-link revocations (and optional known-link history).
 *
 * All methods are async (IndexedDB is inherently async). Multiple instances
 * pointing at the same `idbStore` share state because they share the same IDB
 * database.
 */
export class CallLinkStore {
  private readonly idb: UseStore;

  constructor(options: CallLinkStoreOptions = {}) {
    this.idb =
      options.idbStore ??
      createStore("fr.kaether.call_links", "call_links");
  }

  // --------------------------------------------------------------------------
  // Known links
  // --------------------------------------------------------------------------

  /**
   * Records the link locally. Returns true if the entry is new, false if a
   * known-link record for this roomId already existed. Mirrors iOS
   * `CallLinkStore.recordKnownLink`.
   */
  async recordKnownLink(link: CallLink): Promise<boolean> {
    const roomId = await deriveRoomId(link.secret);
    const key = await knownLinkKey(roomId);
    const existing = await get<StoredKnownLink>(key, this.idb);
    if (existing !== undefined) {
      return false;
    }
    const record: StoredKnownLink = {
      roomId: roomId.asString(),
      ownerUserId: link.ownerUserId,
      ownerPublicKey: base64UrlEncodeBytes(link.ownerPublicKey),
      flags: link.flags,
      createdAt: link.createdAt.toString(),
    };
    await set(key, record, this.idb);
    return true;
  }

  /**
   * Removes the known-link record for `roomId`. Mirrors iOS
   * `CallLinkStore.forgetLink`.
   */
  async forgetLink(roomId: CallLinkRoomId): Promise<void> {
    const key = await knownLinkKey(roomId);
    await del(key, this.idb);
  }

  // --------------------------------------------------------------------------
  // Revocations
  // --------------------------------------------------------------------------

  /**
   * Verifies the revocation signature against `ownerPublicKey` and stores it
   * on success. Returns true if a new revocation was recorded; false if the
   * signature did not verify OR the revocation was already known.
   *
   * CRITICAL: signature is verified BEFORE persist — a forged revocation
   * (signed with the wrong key) is silently dropped. Mirrors iOS
   * `CallLinkStore.recordRevocation`.
   */
  async recordRevocation(
    payload: CallLinkRevocationPayload,
    ownerPublicKey: Uint8Array,
  ): Promise<boolean> {
    const valid = await verifyCallLinkRevocation(payload, ownerPublicKey);
    if (!valid) return false;

    // Reconstruct CallLinkRoomId from the payload's roomId string.
    const roomIdBytes = base64UrlDecode(payload.roomId);
    if (roomIdBytes === null || roomIdBytes.length !== 16) return false;

    let roomId: CallLinkRoomId;
    try {
      roomId = new CallLinkRoomId(roomIdBytes);
    } catch {
      return false;
    }

    const key = await revocationKey(roomId);
    const existing = await get<StoredRevocation>(key, this.idb);
    if (existing !== undefined) {
      return false;
    }

    const record: StoredRevocation = {
      schema: payload.schema,
      roomId: payload.roomId,
      revokedAt: payload.revokedAt,
      ownerSignature: bytesToBase64(payload.ownerSignature),
    };
    await set(key, record, this.idb);
    return true;
  }

  /**
   * Returns true if `roomId` is currently in the local revocation index.
   * Mirrors iOS `CallLinkStore.isRevoked`.
   */
  async isRevoked(roomId: CallLinkRoomId): Promise<boolean> {
    const key = await revocationKey(roomId);
    const v = await get<StoredRevocation>(key, this.idb);
    return v !== undefined;
  }

  /**
   * Snapshot of every revoked roomId currently in the store, as base64url
   * strings (matches `CallLinkRoomId.asString()`). Useful as input to
   * `evaluateCallLinkPolicy`. Mirrors iOS
   * `CallLinkStore.knownRevocationRoomIds`.
   */
  async knownRevocationRoomIds(): Promise<Set<string>> {
    const allKeys = await keys<string>(this.idb);
    const result = new Set<string>();
    for (const k of allKeys) {
      if (typeof k === "string" && k.startsWith("revoke:")) {
        const record = await get<StoredRevocation>(k, this.idb);
        if (record !== undefined) {
          result.add(record.roomId);
        }
      }
    }
    return result;
  }

  // --------------------------------------------------------------------------
  // Reset
  // --------------------------------------------------------------------------

  /**
   * Clears all records from the store. Mirrors iOS `CallLinkStore.resetAll`.
   *
   * NOTE: This removes ALL entries including both known links and revocations,
   * because idb-keyval does not expose a clear-by-prefix operation. Use only
   * in tests or explicit "sign out" flows.
   */
  async resetAll(): Promise<void> {
    const allKeys = await keys<string>(this.idb);
    for (const k of allKeys) {
      await del(k, this.idb);
    }
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function base64UrlEncodeBytes(data: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
